import { AssetSubClassCode } from '@api/api/entity-constants';
import { Entity } from '@api/api/entity-schema';
import { isEtf } from '@api/utils/isEtf';
import { RangeHorizon, ResampleIntervals } from '@chart';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { config, webOnlyFeat } from '@terminal/config/app.config';
import { About } from '@terminal/modules/about/About';
import { LegacyEntityHeader } from '@terminal/modules/entity-header/LegacyEntityHeader';
import { OverviewEventFeed } from '@terminal/modules/overview-event-feed/OverviewEventFeed';
import { OverviewFinancials } from '@terminal/modules/overview-financials/OverviewFinancials';
import { ThemeKnowledgeGraph } from '@terminal/modules/overview-knowledge-graph/components/theme-knowledge-graph/ThemeKnowledgeGraph';
import { OverviewValuation } from '@terminal/modules/overview-valuation-metrics/OverviewValuationMetrics';
import { ChartPrice } from '@terminal/modules/simple-chart/components/chart-price/ChartPrice';
import { SimpleChart } from '@terminal/modules/simple-chart/SimpleChart';
import { useEarningsInsightsByEntities } from '@terminal/react-query-hooks/earnings/useEarningsInsightsByEntities/useEarningsInsightsByEntities';
import { useCompanyTheme } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { useSnakeMeta } from '@terminal/react-query-hooks/snakes/useSnake';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { ChartSettings } from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { BrandsAndProductsSection } from '@terminal/widgets/knowledge-graph-widget/components/brands-and-products-section/BrandsAndProductsSection';
import { CompetitorsSection } from '@terminal/widgets/knowledge-graph-widget/components/competitors-tab/tabs/competitor-detail/components/competitors-section/CompetitorsSection';
import { EarningsInsightSection } from '@terminal/widgets/knowledge-graph-widget/components/earnings-insight-section/EarningsInsightSection';
import { GeographicExposureSection } from '@terminal/widgets/knowledge-graph-widget/components/geographic-exposure-tab/geographic-exposure-section/geographic-exposure-section/GeographicExposureSection';
import { UpgradeToUnlock } from '@terminal/widgets/overview/components/upgrade-to-unlock/UpgradeToUnlock';

import styles from './AssetOverviewWidget.module.scss';
import { useAssetOverviewNavigation } from './hooks/use-asset-overview-navigation/useAssetOverviewNavigation';

export interface AssetOverviewWidgetProps {
  entity: Entity;
  isDrawer?: boolean;
  source: PosthogTrackingSource;
}

export const AssetOverviewWidget = ({
  entity,
  isDrawer = false,
  source,
}: AssetOverviewWidgetProps) => {
  const isBasicUser = useSubscription(state => state.isBasicUser());
  const { data: snakeMeta } = useSnakeMeta(entity.default_snake);
  const { byEntityTag, isLoading: isEarningsLoading } =
    useEarningsInsightsByEntities([entity.tag]);
  const {
    latestEarningsData,
    earningsInsight,
    isPreview = false,
  } = byEntityTag.get(entity.tag) ?? {};

  const { error: companyThemeError } = useCompanyTheme({
    entityTag: entity.tag,
  });

  const { navigateToChart } = useAssetOverviewNavigation(entity, source);

  const chartSettings: ChartSettings = {
    assets: [
      {
        tag: entity.tag,
        snake: entity.default_snake,
        paneIndex: 0,
      },
    ],
    resample: ResampleIntervals.OneDay,
    horizon: RangeHorizon.ThreeMonths,
    domain: null,
    hidden: undefined,
    paneSettings: {},
  };

  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;
  const entityIsEtf = isEtf(entity);
  const displayAbout = isStock || entityIsEtf;
  const displayConnections = companyThemeError?.status !== 404 && isStock;

  return (
    <div className={styles.container} data-testid="asset-overview-widget">
      <LegacyEntityHeader
        entity={entity}
        truncateLongTitle={isDrawer}
        truncateAtLength={40}
        isDrawer={isDrawer}
        headerButton={<></>}
        hideButtonNode={config.app.isTeamsApp}
        className={
          isDrawer ? styles.styledEntityHeaderDrawer : styles.styledEntityHeader
        }
      />
      <div className={styles.contentContainer}>
        <div className={styles.chartChipSelectorWrapper}>
          <ChartPrice entity={entity} snakeMeta={snakeMeta} />
          <SimpleChart
            entity={entity}
            initialChartSettings={chartSettings}
            isLegacyVariant={false}
            onClickFullscreen={webOnlyFeat(navigateToChart)}
            maxHorizon={
              isDrawer ? RangeHorizon.ThreeYears : RangeHorizon.TenYears
            }
            eventsConfig={{
              TMI: true,
            }}
            source={source}
          />
        </div>
        <EarningsInsightSection
          entity={entity}
          earningsData={latestEarningsData}
          earningsInsight={earningsInsight}
          isLoading={isEarningsLoading}
          isPreview={isPreview}
        />
        {isBasicUser ? (
          <UpgradeToUnlock />
        ) : (
          <ErrorBoundaryWrapper>
            <OverviewEventFeed
              entity={entity}
              isDrawer={isDrawer}
              source={source}
            />
          </ErrorBoundaryWrapper>
        )}

        {isDrawer && displayAbout && (
          <About
            entity={entity}
            variant="secondary"
            showMoreDescription
            limit={300}
          />
        )}

        {displayConnections && (
          <>
            <ThemeKnowledgeGraph
              entityTag={entity.tag}
              source={source}
              isInDrawer={isDrawer}
            />
            <CompetitorsSection
              entityTag={entity.tag}
              source={source}
              isInDrawer={isDrawer}
            />
            <GeographicExposureSection
              entityTag={entity.tag}
              source={source}
              isInDrawer={isDrawer}
            />
            <BrandsAndProductsSection
              entityTag={entity.tag}
              source={source}
              isInDrawer={isDrawer}
            />
          </>
        )}
        {isStock && isDrawer && (
          <>
            <OverviewFinancials entity={entity} isDrawer={isDrawer} />
            <OverviewValuation entity={entity} />
          </>
        )}
      </div>
    </div>
  );
};
